import React, {useEffect, useState} from "react";
import {
    Alert,
    Backdrop,
    Button,
    CircularProgress,
    Container,
    Pagination,
    Paper,
    styled,
    Tooltip,
    tooltipClasses
} from "@mui/material";
import {Link, useLocation} from "react-router-dom";
import {ApiUrl, PortalUrl} from "../utils/portal-urls";
import Grid from "@mui/material/Grid";
import RefreshIcon from '@mui/icons-material/Refresh';
import AddIcon from '@mui/icons-material/Add';
import ExternalSourceBreadcrumbs from "../ExternalSourceManagement/ExternalSourceBreadcrumbs";
import {useDispatch, useSelector} from "react-redux";
import {getQueryParams, handleHttpResponse} from "../utils/http-helpers";
import NpiLogsTable from "./NpiLogsTable";
import ShowSnackbar from "../utils/ShowSnackbar";
import clsx from "clsx";
import {batchCurrentStatus} from "../ExternalSourceManagement/ExternalSourceManagementHome";
import {getAllBatches} from "../features/allBatchesSlice";
import ExternalSourceBatchProgressBar from "../ExternalSourceManagement/ExternalSourceBatchProgressBar";

const StyledContainer = styled(Container)(({theme}) => ({
    paddingTop: theme.spacing(4),
    paddingBottom: theme.spacing(4),
}))
const StyledDiv = styled('div')({
    display: "flex",
    float: "right",
})
const StyledPaper = styled(Paper)(({theme}) => ({
    padding: theme.spacing(2),
    boxShadow: '0 7px 20px #cccccc',
    minHeight: 450,
    borderRadius: '15px'
}))
const StyledTooltip = styled(({ className, ...props }) => (
    <Tooltip {...props} arrow classes={{ popper: className }} />
))(({ theme }) => ({
    [`& .${tooltipClasses.arrow}`]: {
        color: theme.palette.primary.main,
    },
    [`& .${tooltipClasses.tooltip}`]: {
        backgroundColor: theme.palette.primary.main,
        fontSize: '14px'
    },
}));
const StyledBatchDiv = styled('div') ({
    marginBottom: '20px',
    '&:last-child': {
        marginBottom: 0,
    }
})


export default function NpiPageHome(props) {

    const getParameters = getQueryParams();

    const [refresh, setRefresh] = useState(new Date().getTime());
    const [loading, setLoading] = useState(false);
    const [npiPageResult, setNpiPageResult] = useState(null);
    const [page, setPage] = useState(getParameters.page ? parseInt(getParameters.page) : 0);
    const [query, setQuery] = useState("");
    const [queryCount, setQueryCount] = useState(0);
    const [currentViewingQuery, setCurrentViewingQuery] = useState(null);
    const [snackbarMsg, setSnackbarMsg] = useState({open: false, msg: '', msgLv: ''});

    const dynamicHeightPaper = clsx(StyledPaper, {minHeight: 120});

    const location = useLocation();
    const path = location.pathname;

    const dispatch = useDispatch();
    // const {npiBatchId, npiStatus } = useSelector(state => state.npiBatch);

    const { nppesBatches, batchRefresh } = useSelector(state => state.allBatches);


    const onRefresh = () => {
        setRefresh(new Date().getTime());
    }

    useEffect(() => {
        console.log("page changed or refresh, useEffect fetch: ", query, page);
        if (query !== currentViewingQuery && queryCount > 0 && page > 0) {
            setPage(0);
        } else {
            fetchNpiLogsPage("NPPESNPI", query, page);
        }
        // getActiveBatchProgress('NPI', setLoading, setSnackbarMsg)
        //     .then(res => {
        //         dispatch(addNpiBatch(res));
        //     })
        dispatch(getAllBatches());
    }, [refresh, query, page, batchRefresh])

    const fetchNpiLogsPage = (source, query, page) => {
        setLoading(true);
        const url = ApiUrl.ExternalSource.getExternalSourceLogs.getPath(source, query, page);
        console.log("Getting Npi logs from: " + url);

        fetch(url)
            .then(handleHttpResponse)
            .catch( err => {
                setLoading(false);
                if (err) {
                    console.error(err);
                    setSnackbarMsg({open: true, msg: 'Failed to Get NPPESNPI Logs', msgLv: 'error'})
                }
            })
            .then( res => {
                setLoading(false);
                if (res) {
                    setNpiPageResult(res);
                    setCurrentViewingQuery(query);
                }

            })
    }

    return (
        <StyledContainer maxWidth='xl'>
            <ExternalSourceBreadcrumbs path={path}/>
            <h3 className="title">
                NPI Management
                <StyledDiv>
                    <Button variant="outlined" component={Link} to={PortalUrl.ExternalSource.NPPESNPI.pattern_new_task} endIcon={<AddIcon />}>New
                        Import Task</Button>
                    <Button variant="outlined" onClick={() => onRefresh()} endIcon={<RefreshIcon />}>Refresh</Button>
                </StyledDiv>
            </h3>

            <Grid container spacing={3}>
                <Grid item xs={12} md={12} lg={12}>
                    <h4>All NPI Logs</h4>
                    <StyledPaper>
                        {npiPageResult?.content && npiPageResult.content.length > 0 &&
                            <div>
                                <NpiLogsTable npiLogs={npiPageResult.content} />
                                <br/>
                                {npiPageResult.totalPages >= 1 &&
                                    <Pagination page={page + 1} count={npiPageResult.totalPages} variant="outlined" color="primary" onChange={(e, newPage) => setPage(newPage - 1)}/>}
                            </div>
                        }
                    </StyledPaper>
                </Grid>
                <Grid item xs={12} md={12} lg={12}>
                    <h4 className="widgetTitle">Current NPI Progress Information</h4>
                    {nppesBatches?.length > 0 ?
                        nppesBatches.map( b => (
                            <StyledBatchDiv key={b.id}>
                                <Paper className={dynamicHeightPaper} sx={{ padding: (theme) => theme.spacing(2), minHeight: 200}}>
                                    {b.status && <Alert severity={batchCurrentStatus[b.status].getStatus} icon={false}>{batchCurrentStatus[b.status].getMsg("NPPESNPI")}</Alert> }
                                    <br/>

                                    { b.id && b.status !== 'Completed' && b.status !== 'Cancelled' && b.status !== 'Failed' &&
                                        // <NpiBatchProgressBar batch={b}/>}
                                    <ExternalSourceBatchProgressBar batch={b}/>}

                                </Paper>
                            </StyledBatchDiv>
                        )) :
                        <Paper className={dynamicHeightPaper} sx={{ padding: (theme) => theme.spacing(2), minHeight: 200}}>
                            {<Alert severity='info' icon={false}>No Active Process</Alert> }
                        </Paper>
                    }

                    {/*{Object.keys(nppesBatches).length > 0 ?*/}
                    {/*    Object.entries(nppesBatches).map(([key, value]) => (*/}
                    {/*        <StyledBatchDiv key={key}>*/}
                    {/*            <Paper className={dynamicHeightPaper} sx={{ padding: (theme) => theme.spacing(2), minHeight: 200}}>*/}
                    {/*                {value.status && <Alert severity={batchCurrentStatus[value.status].getStatus} icon={false}>{batchCurrentStatus[value.status].getMsg("NPPESNPI")}</Alert> }*/}
                    {/*                <br/>*/}

                    {/*                { key && value.status !== 'Completed' && value.status !== 'Cancelled' && value.status !== 'Failed' &&*/}
                    {/*                    <NpiBatchProgressBar batch={value}/>}*/}

                    {/*            </Paper>*/}
                    {/*        </StyledBatchDiv>*/}
                    {/*    )) :*/}
                    {/*    <Paper className={dynamicHeightPaper} sx={{ padding: (theme) => theme.spacing(2), minHeight: 200}}>*/}
                    {/*        {<Alert severity='info' icon={false}>No Active Process</Alert> }*/}
                    {/*    </Paper>*/}
                    {/*}*/}
                </Grid>
            </Grid>
            <ShowSnackbar open={snackbarMsg.open} close={(c) => setSnackbarMsg(c)} msg={snackbarMsg} />
            <Backdrop sx={{zIndex: theme => theme.zIndex.drawer + 1, color: "#fff"}} open={loading}>
                <CircularProgress color={"inherit"}/>
            </Backdrop>
        </StyledContainer>
    )

}
